/**
 * Landing Page V2 - Sidebar Navigation
 */

import React from 'react';
import { useRouter } from 'next/router';
import Image from 'next/image';
import {
  backgroundColors,
  spacing,
  typography,
  fontPrimary,
  brandColors,
} from '@/design-system/utils';
import { useChatScreenStore } from '@/store/chatScreenStore';
import { useAuthTokens } from '@/utils/tokenHelper';
import { useUserStore } from '@/store/userStore';
import { safeRouterPush } from '@/utils/safeRouterPush';
import clsx from 'clsx';
import { WebNavbarItem, NewChatButton } from './items';
import { NavbarLabel, ProfileMenu } from '../shared';

interface MenuItem {
  id: string;
  label: string;
  iconName: string;
  badge?: string;
}

const menuItems: MenuItem[] = [
  {
    id: 'experiences',
    label: 'Experiences',
    iconName: 'experiences',
  },
  {
    id: 'discover',
    label: 'Discover',
    iconName: 'discover',
  },
  {
    id: 'shop',
    label: 'Shop',
    iconName: 'shop',
    badge: 'New',
  },
  {
    id: 'blog',
    label: 'Blog',
    iconName: 'blog',
  },
  {
    id: 'history',
    label: 'History',
    iconName: 'history',
  },
  {
    id: 'riders',
    label: 'Riders Corner',
    iconName: 'riders',
  },
];

export interface SidebarProps {
  onNewChat?: () => void;
  onLogoClick?: () => void;
  className?: string;
}

export function Sidebar({
  onNewChat,
  onLogoClick,
  className,
}: SidebarProps = {}) {
  const router = useRouter();
  const [activeMenu, setActiveMenu] = React.useState<string>('discover');
  const [activeAction, setActiveAction] = React.useState<'profile' | null>(
    null
  );
  const [profileMenuAnchor, setProfileMenuAnchor] =
    React.useState<HTMLElement | null>(null);
  const [imageError, setImageError] = React.useState(false);
  const { conversations, setActiveConversation, getActiveConversation } =
    useChatScreenStore();
  const { accessToken, refreshToken } = useAuthTokens();
  const { name, photo } = useUserStore();

  const isLoggedIn = !!accessToken && !!refreshToken;
  // Show profile initial when we have a real name (from rehydrated store); otherwise 'U'
  const avatarLetter =
    name && name !== 'User Name' ? name.charAt(0).toUpperCase() : 'U';

  // Debug: navbar profile state (filter console by [NAVBAR_DEBUG])
  React.useEffect(() => {
    console.log('[NAVBAR_DEBUG] Sidebar', {
      name,
      hasPhoto: !!photo,
      isLoggedIn,
      avatarLetter,
    });
  }, [name, photo, isLoggedIn, avatarLetter]);

  // Reset image error when photo changes
  React.useEffect(() => {
    setImageError(false);
  }, [photo]);

  // Update active menu based on current route
  React.useEffect(() => {
    const path = router.pathname;
    if (path === '/experiences') {
      setActiveMenu('experiences');
    } else if (path === '/chat') {
      setActiveMenu('discover');
    } else if (path === '/history') {
      setActiveMenu('history');
    } else if (
      path === '/riders-corner' ||
      path.startsWith('/riders-corner/')
    ) {
      setActiveMenu('riders');
    }
  }, [router.pathname]);

  const handleMenuClick = (menuId: string) => {
    setActiveMenu(menuId);

    // Clear all filter-related localStorage whenever any navbar item is clicked
    if (typeof window !== 'undefined') {
      try {
        localStorage.removeItem('nexgo-experience-filters');
        localStorage.removeItem('nexgo-experience-temp-filters');
        localStorage.removeItem('nexgo-experience-selected-filter');
        localStorage.removeItem('nexgo-experience-search-query');
      } catch (error) {
        console.warn('Failed to clear filter localStorage:', error);
      }
    }

    // Navigate based on menu item (safeRouterPush avoids stuck soft transitions
    // after leaving heavy detail pages where the URL can update without a UI swap)
    if (menuId === 'experiences') {
      void safeRouterPush(router, '/experiences');
    } else if (menuId === 'discover') {
      // Check for active conversation first (the one that was there before navigating away)
      const activeConversation = getActiveConversation();

      if (activeConversation && activeConversation.messages.length > 0) {
        // Restore the active conversation
        setActiveConversation(activeConversation.id);
        // Navigate to chat and pass conversation data via query params
        void safeRouterPush(router, {
          pathname: '/chat',
          query: {
            conversationId: activeConversation.id,
            sessionId: activeConversation.sessionId,
          },
        });
      } else if (conversations.length > 0) {
        // Fallback: if no active conversation, use the most recent conversation
        const mostRecentConversation = conversations[0];
        setActiveConversation(mostRecentConversation.id);
        void safeRouterPush(router, {
          pathname: '/chat',
          query: {
            conversationId: mostRecentConversation.id,
            sessionId: mostRecentConversation.sessionId,
          },
        });
      } else {
        // No conversations, just navigate to chat (will show landing page)
        void safeRouterPush(router, '/chat');
      }
    } else if (menuId === 'history') {
      void safeRouterPush(router, '/history');
    } else if (menuId === 'blog') {
      // Navigate to external blog site
      window.open('https://blogs.nexgo.ai/', '_blank', 'noopener,noreferrer');
    } else if (menuId === 'riders') {
      void safeRouterPush(router, '/riders-corner');
    }
    // Add other navigation cases as needed
  };

  return (
    <aside
      style={{
        backgroundColor: backgroundColors.primary,
        borderRightColor: backgroundColors.shade,
        paddingLeft: spacing[2],
        paddingRight: spacing[2],
        paddingTop: spacing[6],
        paddingBottom: spacing[12],
      }}
      className={clsx(
        'hidden md:flex fixed left-0 top-0 h-screen w-[111px] border-r flex-col items-center z-[100]',
        className
      )}
    >
      {/* Logo - Fixed at top */}
      <div
        style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexShrink: 0,
          marginBottom: spacing[16], // 32px gap between logo and menu items
        }}
      >
        <button
          onClick={onLogoClick}
          className='cursor-pointer hover:opacity-80 transition-opacity'
          aria-label='Go to landing page'
          style={{
            width: '72px',
            height: '24px',
            aspectRatio: '3/1',
          }}
        >
          <Image
            src='/assets/images/logo.svg'
            alt='Nexgo'
            width={72}
            height={24}
            className='object-cover object-[50%_50%]'
            style={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              objectPosition: '50% 50%',
            }}
            unoptimized
          />
        </button>
      </div>

      {/* Menu Items - Scrollable */}
      <nav
        className='flex flex-col items-center overflow-y-auto navbar-scrollbar'
        style={{
          flex: 1,
          width: '100%',
          minHeight: 0, // Important for flex scrolling
          gap: spacing[6], // 24px gap between all menu items (including New Chat)
        }}
      >
        {/* New Chat Button */}
        <div
          style={{
            display: 'none',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            gap: spacing[2.5], // 10px - matches var(--spacing-padding-margins-25, 10px)
            flexShrink: 0,
          }}
        >
          <NewChatButton
            onClick={() => {
              console.log('🔄 [Sidebar] New Chat button clicked');
              onNewChat?.();
            }}
          />
          <NavbarLabel style={{ whiteSpace: 'nowrap' }}>New Chat</NavbarLabel>
        </div>

        {/* Menu Items */}
        <div className='flex flex-col items-center' style={{ gap: spacing[6] }}>
          {menuItems
            .filter(item => item.id !== 'shop')
            .map(item => {
              const isActive = activeMenu === item.id;

              return (
                <WebNavbarItem
                  key={item.id}
                  id={item.id}
                  label={item.label}
                  iconName={item.iconName}
                  isActive={isActive}
                  onClick={() => handleMenuClick(item.id)}
                  badge={item.badge}
                />
              );
            })}
        </div>
      </nav>

      {/* Profile Avatar - Fixed at bottom */}
      <div
        className='flex flex-col items-center flex-shrink-0'
        style={{
          gap: spacing[3],
          marginTop: 'auto',
          paddingBottom: spacing[4],
        }}
      >
        <button
          type='button'
          ref={el => {
            setProfileMenuAnchor(el);
          }}
          onClick={() => {
            setActiveAction(activeAction === 'profile' ? null : 'profile');
          }}
          className='flex flex-col items-center outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-[#0A0322]/30'
          style={{ gap: spacing[1.5] }}
          aria-pressed={activeAction === 'profile'}
          aria-label='Profile'
        >
          <div
            className='relative flex items-center justify-center'
            style={{
              boxShadow: '0px 0px 0px rgba(0,0,0,0)',
            }}
          >
            <div
              style={{
                width: '48px',
                height: '48px',
                borderRadius: '50%',
                background: photo
                  ? 'transparent'
                  : `linear-gradient(135deg, ${brandColors.primary} 0%, ${brandColors.secondary} 100%)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: fontPrimary,
                fontSize: typography.size.lg,
                fontWeight: typography.weight.bold,
                color: '#1C1917',
                border: '2px solid rgba(255, 255, 255, 0.3)',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                overflow: 'hidden',
              }}
            >
              {photo && !imageError ? (
                <Image
                  src={photo}
                  alt={name || 'User'}
                  width={48}
                  height={48}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                  unoptimized
                  onError={() => setImageError(true)}
                />
              ) : (
                avatarLetter
              )}
            </div>
          </div>
        </button>
        <ProfileMenu
          isOpen={activeAction === 'profile'}
          onClose={() => setActiveAction(null)}
          anchorElement={profileMenuAnchor}
          position='top'
        />
      </div>
    </aside>
  );
}
